import { useState, type FormEvent } from 'react';
import { ApiError } from '../../../api/client';
import { publico } from '../../../api/publico';
import { usePedido } from '../../../api/usePedido';
import { Enlace, irA } from '../../../router';
import type { ScreenProps } from '../../../routes/types';
import { useSesion } from '../../../session/Sesion';
import { AvisoError, Boton, Casilla, Entrada, FechaDeNacimiento, Marco, Selector } from '../../../ui';
import { useFormulario } from '../../../forms/useFormulario';
import {
  LARGOS, separarNombre, soloDigitos, validarClave, validarDni, validarEmail, validarNacimiento,
  validarNombreCompleto, validarTelefono,
} from '../../../forms/validacion';
import { AvisoDeContexto, CampoClave, MedidorDeFuerza, destinoDespuesDeIngresar } from './piezas';

export function Registro({ consulta }: ScreenProps) {
  const { ingresar } = useSesion();
  const volverA = consulta.get('volverA');
  const { datos: catalogos } = usePedido(() => publico.catalogos(), []);
  const [acepta, setAcepta] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const formulario = useFormulario(
    { nombre: '', email: '', dni: '', nacimiento: '', telefono: '', obraSocial: '', clave: '', repetir: '' },
    {
      nombre: validarNombreCompleto, email: validarEmail, dni: validarDni, nacimiento: validarNacimiento,
      telefono: validarTelefono, clave: validarClave,
      repetir: (v, todos) => (!v ? 'Repetí la contraseña.' : v !== todos.clave ? 'Las contraseñas no coinciden.' : null),
    },
    { nombre: LARGOS.nombre, email: LARGOS.email, clave: LARGOS.clave, repetir: LARGOS.clave },
  );
  const { valores } = formulario;

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!formulario.validarTodo()) return;
    if (!acepta) {
      setError('Para crear la cuenta tenés que aceptar los términos y condiciones.');
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      await publico.registrarPaciente({
        ...separarNombre(valores.nombre), email: valores.email.trim(), dni: valores.dni,
        fecha_nacimiento: valores.nacimiento, telefono: valores.telefono,
        obra_social: valores.obraSocial || undefined, clave: valores.clave, acepta_terminos: true,
      });
      const usuario = await ingresar(valores.email.trim(), valores.clave);
      irA(destinoDespuesDeIngresar(usuario.rol, volverA), { reemplazar: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos crear la cuenta. Probá de nuevo en un momento.');
      setEnviando(false);
    }
  };

  return (
    <Marco
      titulo="Creá tu cuenta"
      bajada="Reservá turnos con profesionales verificados y recibí los recordatorios por WhatsApp."
      pie={<>¿Ya tenés cuenta? <Enlace a={volverA ? `/login?volverA=${encodeURIComponent(volverA)}` : '/login'}>Entrá</Enlace></>}
    >
      <AvisoDeContexto volverA={volverA} />
      <form onSubmit={(e) => void enviar(e)} noValidate className="acceso__formulario">
        <AvisoError>{error}</AvisoError>
        <Entrada etiqueta="Nombre y apellido" autoComplete="name" required {...formulario.campo('nombre')} />
        <Entrada etiqueta="Correo electrónico" type="email" autoComplete="email" required {...formulario.campo('email')} />
        <div className="acceso__fila">
          <Entrada etiqueta="DNI" inputMode="numeric" required {...formulario.campo('dni')}
            onChange={(e) => formulario.cambiar('dni', soloDigitos(e.target.value, LARGOS.dni))} />
          <Entrada etiqueta="Teléfono" inputMode="tel" ayuda="Te mandamos los recordatorios por WhatsApp." required
            {...formulario.campo('telefono')}
            onChange={(e) => formulario.cambiar('telefono', soloDigitos(e.target.value, LARGOS.telefono))} />
        </div>
        <FechaDeNacimiento required value={formulario.valores.nacimiento} error={formulario.campo('nacimiento').error}
          onBlur={formulario.campo('nacimiento').onBlur} alCambiar={(fecha) => formulario.cambiar('nacimiento', fecha)} />
        <Selector etiqueta="Obra social" marcador="No tengo / particular"
          opciones={catalogos?.obras_sociales ?? []} {...formulario.campo('obraSocial')} />
        <div>
          <CampoClave etiqueta="Contraseña" autoComplete="new-password" required
            ayuda="Al menos 8 caracteres, con letras y números." {...formulario.campo('clave')} />
          <MedidorDeFuerza clave={valores.clave} />
        </div>
        <CampoClave etiqueta="Repetir contraseña" autoComplete="new-password" required {...formulario.campo('repetir')} />
        <Casilla checked={acepta} onChange={(e) => setAcepta(e.target.checked)}
          etiqueta="Acepto los términos y condiciones y la política de privacidad" />
        <Boton type="submit" tamano="grande" anchoCompleto cargando={enviando}>Crear cuenta</Boton>
      </form>
      <p className="acceso__nota">¿Sos profesional de la salud? <Enlace a="/registro-profesional">Sumá tu consultorio</Enlace></p>
    </Marco>
  );
}
